/**
 * Stripe Configuration
 * Client-side Stripe integration for payments and subscriptions
 */

import { loadStripe } from '@stripe/stripe-js'

// Load Stripe with publishable key
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY

if (!stripePublishableKey) {
  console.warn('Stripe publishable key not configured. Payment features will be disabled.')
}

// Initialize Stripe (client-side)
export const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null

export type SubscriptionTier = 'free_trial' | 'starter' | 'professional' | 'enterprise'
// Note: 'starter' kept for backward compatibility but no longer offered as a plan
export type BillingInterval = 'monthly' | 'annual'

export interface PlanConfig {
  tier: SubscriptionTier
  name: string
  description: string
  monthlyPrice: number
  annualPrice: number
  monthlyPriceId: string
  annualPriceId: string
  limits: {
    users: number
    products: number
    documents: number
    ai_tokens: number
  }
  features: string[]
  highlighted?: boolean
}

export const PLANS: Record<Exclude<SubscriptionTier, 'free_trial'>, PlanConfig> = {
  starter: {
    tier: 'starter',
    name: 'Starter',
    description: 'For small teams getting started with compliance',
    monthlyPrice: 79,
    annualPrice: 790,
    monthlyPriceId: 'price_1T1tVEPH6ReI0dscyUieNBtn',
    annualPriceId: 'price_1T1tVEPH6ReI0dscwl8CCoyY',
    limits: { users: 3, products: 1, documents: 100, ai_tokens: 50_000 },
    features: [
      '3 users',
      '1 product',
      'Basic compliance checklists',
      'Document storage (100 docs)',
      'Email support',
    ],
  },
  professional: {
    tier: 'professional',
    name: 'Professional',
    description: 'Full QMS suite for growing manufacturers',
    monthlyPrice: 199,
    annualPrice: 1990,
    monthlyPriceId: 'price_1T1tVFPH6ReI0dscjVutM9On',
    annualPriceId: 'price_1T1tVFPH6ReI0dscFsTUeuoF',
    limits: { users: 10, products: -1, documents: -1, ai_tokens: 500_000 },
    features: [
      '10 users',
      'Unlimited products',
      'Full QMS suite',
      'ISO 9001 compliance tracking',
      'Priority support',
      'API access',
    ],
    highlighted: true,
  },
  enterprise: {
    tier: 'enterprise',
    name: 'Enterprise',
    description: 'Everything unlimited with dedicated support',
    monthlyPrice: 399,
    annualPrice: 3990,
    monthlyPriceId: 'price_1T1tVFPH6ReI0dscgX0OOISI',
    annualPriceId: 'price_1T1tVGPH6ReI0dscDFtm2zQl',
    limits: { users: -1, products: -1, documents: -1, ai_tokens: -1 },
    features: [
      'Unlimited users',
      'Unlimited everything',
      'Dedicated support',
      'Custom integrations',
      'Audit preparation tools',
      'White-label reports',
      'SSO',
    ],
  },
}

// Keep backward compatibility
export const STRIPE_PRODUCTS = {
  FREE: {
    tier: 'free' as const,
    name: 'Free',
    price: 0,
    features: ['core-auth', 'core-documents', 'document-intelligence'],
  },
  STARTER: {
    tier: 'starter' as const,
    name: 'Starter',
    monthlyPriceId: PLANS.starter.monthlyPriceId,
    annualPriceId: PLANS.starter.annualPriceId,
    monthlyPrice: PLANS.starter.monthlyPrice,
    annualPrice: PLANS.starter.annualPrice,
    features: [
      'core-auth', 'core-documents', 'document-intelligence',
      'document-search', 'document-sharing', 'expiry-tracking',
    ],
  },
  PROFESSIONAL: {
    tier: 'professional' as const,
    name: 'Professional',
    monthlyPriceId: PLANS.professional.monthlyPriceId,
    annualPriceId: PLANS.professional.annualPriceId,
    monthlyPrice: PLANS.professional.monthlyPrice,
    annualPrice: PLANS.professional.annualPrice,
    features: [
      'core-auth', 'core-documents', 'document-intelligence',
      'document-search', 'document-sharing', 'expiry-tracking',
      'core-products', 'core-checklists', 'supplier-management',
      'certificate-manager', 'export-reports', 'qms',
    ],
  },
  ENTERPRISE: {
    tier: 'enterprise' as const,
    name: 'Enterprise',
    monthlyPriceId: PLANS.enterprise.monthlyPriceId,
    annualPriceId: PLANS.enterprise.annualPriceId,
    monthlyPrice: PLANS.enterprise.monthlyPrice,
    annualPrice: PLANS.enterprise.annualPrice,
    features: [
      'core-auth', 'core-documents', 'document-intelligence',
      'document-search', 'document-sharing', 'expiry-tracking',
      'core-products', 'core-checklists', 'supplier-management',
      'certificate-manager', 'export-reports', 'qms',
      'bom-professional', 'iso-compliance', 'api-access',
      'analytics-dashboard', 'bulk-import', 'email-notifications',
    ],
  },
} as const

export function getTierFeatures(tier: string): readonly string[] {
  switch (tier) {
    case 'free':
      return STRIPE_PRODUCTS.FREE.features
    case 'free_trial':
      // Trial users get full Professional access for the trial period
      return STRIPE_PRODUCTS.PROFESSIONAL.features
    case 'starter':
      return STRIPE_PRODUCTS.STARTER.features
    case 'professional':
      return STRIPE_PRODUCTS.PROFESSIONAL.features
    case 'enterprise':
      return STRIPE_PRODUCTS.ENTERPRISE.features
    default:
      return STRIPE_PRODUCTS.FREE.features
  }
}

export function getTierPricing(tier: string, billing: BillingInterval) {
  const key = tier.toUpperCase() as keyof typeof STRIPE_PRODUCTS
  const product = STRIPE_PRODUCTS[key]
  if (!product || tier === 'free' || tier === 'free_trial') {
    return { price: 0, priceId: null }
  }
  if ('monthlyPriceId' in product) {
    return {
      price: billing === 'monthly' ? product.monthlyPrice : product.annualPrice,
      priceId: billing === 'monthly' ? product.monthlyPriceId : product.annualPriceId,
    }
  }
  return { price: 0, priceId: null }
}

export function getPlanLimits(tier: SubscriptionTier) {
  if (tier === 'free_trial') {
    // Trial users get Professional-level limits
    return { ...PLANS.professional.limits, ai_tokens: 500_000 }
  }
  const plan = PLANS[tier as keyof typeof PLANS]
  return plan?.limits ?? { users: 1, products: 1, documents: 10, ai_tokens: 0 }
}
